// Photo viewer gestures (Owner task book 2026-09-04 Round 2 §U), as a pure controller so the rules
// can be tested without touch events:
//   one finger at 1×      → horizontal swipe = previous / next photo (vertical moves ignored)
//   two fingers           → pinch = zoom about the pinch midpoint; midpoint drag = pan
//   one finger when zoomed → pan (swipe navigation is off until the photo is back near 1×)
//   double-tap            → toggle 1× ↔ 2.5× about the tapped point
//   pinch ending below ~1.05× → snaps back to exactly 1× (navigation resumes)
// Points are given RELATIVE TO THE STAGE CENTRE (the transform origin of the photo).
export interface Point {
  x: number;
  y: number;
}

export interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

export interface GestureCallbacks {
  onTransform: (transform: Transform) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;
export const ZOOM_DOUBLE_TAP = 2.5;
// Below this the photo counts as "at 1×": it snaps back and swipes navigate again.
export const ZOOM_SNAP = 1.05;
export const SWIPE_MIN_PX = 40;
export const TAP_MAX_PX = 12;
export const DOUBLE_TAP_MS = 320;

export const IDENTITY: Transform = Object.freeze({ scale: 1, tx: 0, ty: 0 });

type Mode = 'none' | 'swipe' | 'pan' | 'pinch';

interface Session {
  mode: Mode;
  start: Point[];
  last: Point[];
  startTransform: Transform;
  startDistance: number;
  startMid: Point;
  moved: boolean;
}

export interface GestureController {
  start(points: Point[]): void;
  move(points: Point[]): void;
  end(remaining: Point[]): void;
  cancel(): void;
  toggleZoomAt(point: Point): void;
  zoomTo(scale: number): void;
  reset(): void;
  transform(): Transform;
  // True while a gesture that owns the touch (pinch / pan) is active — the host cancels default.
  consuming(): boolean;
  isZoomed(): boolean;
  setStageSize(width: number, height: number): void;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function clampScale(scale: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale));
}

export function createGestureController(callbacks: GestureCallbacks, options: { now?: () => number } = {}): GestureController {
  const now = options.now ?? (() => Date.now());
  let transform: Transform = { ...IDENTITY };
  let session: Session | null = null;
  let lastTap: { at: number; point: Point } | null = null;
  let stage = { width: 0, height: 0 };

  function emit(next: Transform): void {
    transform = next;
    callbacks.onTransform(next);
  }

  // Keep the pan inside sensible bounds so the photo never disappears off the stage.
  function bounded(next: Transform): Transform {
    if (stage.width === 0 || stage.height === 0) return next;
    const maxX = ((next.scale - 1) * stage.width) / 2 + 24;
    const maxY = ((next.scale - 1) * stage.height) / 2 + 24;
    return { scale: next.scale, tx: Math.max(-maxX, Math.min(maxX, next.tx)), ty: Math.max(-maxY, Math.min(maxY, next.ty)) };
  }

  function snapIfNearOne(): void {
    if (transform.scale < ZOOM_SNAP) emit({ ...IDENTITY });
  }

  function beginSingle(point: Point): void {
    session = { mode: transform.scale >= ZOOM_SNAP ? 'pan' : 'swipe', start: [point], last: [point], startTransform: { ...transform }, startDistance: 0, startMid: point, moved: false };
  }

  function beginPinch(a: Point, b: Point): void {
    session = { mode: 'pinch', start: [a, b], last: [a, b], startTransform: { ...transform }, startDistance: Math.max(1, distance(a, b)), startMid: midpoint(a, b), moved: true };
  }

  function toggleZoomAt(point: Point): void {
    if (transform.scale >= ZOOM_SNAP) {
      emit({ ...IDENTITY });
      return;
    }
    // t' = P − (P − t)·(s'/s) keeps the tapped point still under the finger.
    const scale = ZOOM_DOUBLE_TAP;
    emit(bounded({ scale, tx: point.x - (point.x - transform.tx) * scale, ty: point.y - (point.y - transform.ty) * scale }));
  }

  return {
    start(points) {
      if (points.length >= 2) beginPinch(points[0], points[1]);
      else if (points.length === 1) beginSingle(points[0]);
    },
    move(points) {
      if (!session) return;
      if (points.length >= 2) {
        if (session.mode !== 'pinch') beginPinch(points[0], points[1]);
        const s = session;
        const ratio = Math.max(1, distance(points[0], points[1])) / s.startDistance;
        const scale = clampScale(s.startTransform.scale * ratio);
        const mid = midpoint(points[0], points[1]);
        const factor = scale / s.startTransform.scale;
        emit(bounded({ scale, tx: mid.x - (s.startMid.x - s.startTransform.tx) * factor, ty: mid.y - (s.startMid.y - s.startTransform.ty) * factor }));
        s.last = [points[0], points[1]];
        s.moved = true;
        return;
      }
      if (points.length !== 1) return;
      const point = points[0];
      if (session.mode === 'pinch') {
        // One finger lifted mid-pinch: continue as a pan from here.
        session = { mode: transform.scale >= ZOOM_SNAP ? 'pan' : 'swipe', start: [point], last: [point], startTransform: { ...transform }, startDistance: 0, startMid: point, moved: true };
        return;
      }
      const dx = point.x - session.start[0].x;
      const dy = point.y - session.start[0].y;
      if (Math.hypot(dx, dy) > TAP_MAX_PX) session.moved = true;
      session.last = [point];
      if (session.mode === 'pan') emit(bounded({ scale: session.startTransform.scale, tx: session.startTransform.tx + dx, ty: session.startTransform.ty + dy }));
    },
    end(remaining) {
      if (!session) return;
      const s = session;
      if (s.mode === 'pinch') {
        if (remaining.length === 1) {
          session = { mode: transform.scale >= ZOOM_SNAP ? 'pan' : 'swipe', start: [remaining[0]], last: [remaining[0]], startTransform: { ...transform }, startDistance: 0, startMid: remaining[0], moved: true };
          return;
        }
        session = null;
        snapIfNearOne();
        return;
      }
      session = null;
      const end = s.last[0];
      const dx = end.x - s.start[0].x;
      const dy = end.y - s.start[0].y;
      if (s.mode === 'swipe') {
        if (Math.abs(dx) >= SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy)) {
          lastTap = null;
          callbacks.onNavigate(dx < 0 ? 'next' : 'prev');
          return;
        }
      }
      if (!s.moved) {
        const at = now();
        if (lastTap && at - lastTap.at <= DOUBLE_TAP_MS && distance(lastTap.point, end) <= 30) {
          lastTap = null;
          toggleZoomAt(end);
          return;
        }
        lastTap = { at, point: end };
        return;
      }
      if (s.mode === 'pan') snapIfNearOne();
    },
    cancel() {
      session = null;
      snapIfNearOne();
    },
    toggleZoomAt,
    zoomTo(scale) {
      const next = clampScale(scale);
      if (next < ZOOM_SNAP) emit({ ...IDENTITY });
      else emit(bounded({ scale: next, tx: (transform.tx * next) / transform.scale, ty: (transform.ty * next) / transform.scale }));
    },
    reset() {
      session = null;
      emit({ ...IDENTITY });
    },
    transform: () => transform,
    consuming: () => session !== null && (session.mode === 'pinch' || session.mode === 'pan'),
    isZoomed: () => transform.scale >= ZOOM_SNAP,
    setStageSize(width, height) {
      stage = { width, height };
    }
  };
}
